import {
  AddPhotoAlternate,
  Check,
  Delete,
  Edit,
  FileUpload,
  Refresh,
  Restore,
} from "@mui/icons-material";
import { Avatar, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import ServiceTypeDeleteConfimationDialog from "./ServiceTypeDeleteConfimationDialog";

const ServiceTypeInputChip = ({
  edit = false,
  type,
  setServiceTypes,
  fetchedServiceTypes = [],
  setServiceTypeIdsToDelete = () => { },
  isDefault = false // NEW: Added isDefault prop
}) => {
  const [title, setTitle] = React.useState(type.name);
  const [contentEditing, setContentEditing] = React.useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (type.name) {
      setTitle(type.name);
    }
  }, [type.name, contentEditing]);

  const onSubmit = () => {
    setContentEditing(false);

    if (!title.trim()) {
      toast.error("Title cannot be empty");
      return;
    }

    setServiceTypes((prev) =>
      prev.map((item) =>
        item.id === type.id ? { ...item, name: title } : item
      )
    );
  };

  const onDelete = (id) => {
    if (edit) setServiceTypeIdsToDelete((prev) => [...prev, id]);
    setServiceTypes((prev) => prev.filter((item) => item.id !== id));
  };

  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const fileUrl = file ? URL.createObjectURL(file) : type.preview || null;

  const deSelectFile = () => {
    setFile(null);
    setServiceTypes((prev) =>
      prev.map((item) =>
        item.id === type.id
          ? { ...item, file: null, preview: fetchedServiceTypes.find(fetched => fetched.id === type.id)?.preview || null }
          : item
      )
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Block SVG uploads
    if (
      file.type === "image/svg+xml" ||
      file.name.toLowerCase().endsWith(".svg")
    ) {
      toast.error("SVG files are not allowed as service type icons");
      e.target.value = "";
      return;
    }

    setFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setServiceTypes((prev) =>
        prev.map((item) =>
          item.id === type.id
            ? { ...item, file, preview: reader.result }
            : item
        )
      );
    };
    reader.readAsDataURL(file);
  };

  // NEW: Only lock if edit mode AND isDefault (not during initial creation)
  const isLocked = edit && isDefault;

  // NEW: Show tooltip only when locked
  const tooltipMessage = isLocked
    ? "Default service types cannot be removed or modified"
    : "";

  return (
    <Tooltip title={tooltipMessage} arrow placement="top">
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          gap: 1,
          borderRadius: 2,
          opacity: isLocked ? 0.8 : 1, // Only reduce opacity when locked
        }}
        elevation={1}
      >
        <input
          type="file"
          hidden
          ref={fileRef}
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          onChange={handleFileChange}
          disabled={isLocked} // Only disable when locked
        />

        <Tooltip title={isLocked ? "" : `${fileUrl ? "Change" : "Upload"} Image`}>
          <div style={{ cursor: isLocked ? "not-allowed" : "pointer", position: "relative" }}>
            <Avatar
              variant="rounded"
              src={fileUrl}
              onClick={() => {
                if (!isLocked) fileRef.current.click();
              }}
            >
              <IconButton
                size="medium"
                variant="contained"
                color="default"
                disabled={isLocked}
              >
                <AddPhotoAlternate />
              </IconButton>
            </Avatar>
            {file && !isLocked && (
              <IconButton
                onClick={deSelectFile}
                sx={{
                  transform: 'scale(0.7)',
                  position: 'absolute',
                  bottom: '-25%',
                  right: '-25%',
                  bgcolor: theme => `${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]}!important`
                }}
                size="small"
                variant="contained"
              >
                <Restore />
              </IconButton>
            )}
          </div>
        </Tooltip>

        {contentEditing ? (
          <input
            type="text"
            value={title}
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              onSubmit();
            }}
            placeholder="Enter Title"
            disabled={isLocked}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "16px",
              cursor: isLocked ? "not-allowed" : "text",
            }}
          />
        ) : (
          <Typography
            variant="h6"
            fontSize={16}
            pl={1}
            onClick={() => {
              if (!isLocked) setContentEditing(true);
            }}
            sx={{ cursor: isLocked ? "not-allowed" : "pointer" }}
          >
            {title || "Click to Enter Title"}
          </Typography>
        )}
        {contentEditing && title && !isLocked && (
          <IconButton
            size="small"
            color="success"
            onClick={() => {
              onSubmit();
            }}
          >
            <Check />
          </IconButton>
        )}
        {!isLocked && (
          <IconButton
            size="small"
            color="error"
            onClick={() => setOpen(true)}
          >
            <Delete />
          </IconButton>
        )}
        <ServiceTypeDeleteConfimationDialog
          open={open}
          setOpen={setOpen}
          onSubmit={() => onDelete(type.id)}
        />
      </Paper>
    </Tooltip>
  );
};

export default ServiceTypeInputChip;
